/**
 * useSyntheticClickGuard
 * Public CTAs activate on touchend, and the browser then synthesizes a click for the same tap. This
 * hook remembers the last touch activation so the click handler can skip that follow-up click.
 */

import { useCallback, useRef } from "react";
import { SYNTHETIC_CLICK_WINDOW_MS } from "../_logic/constants";

/** Callbacks returned by {@link useSyntheticClickGuard}. */
export interface SyntheticClickGuard {
  /** Returns true when a click lands within the synthetic-click window of the last touch activation */
  shouldIgnoreSyntheticClick: () => boolean;
  /** Records a touch activation. Call it from the touchend handler. */
  markTouchActivation: () => void;
}

/**
 * Tracks the last touch activation so a click handler can ignore the synthetic click that follows it.
 * @returns Stable `shouldIgnoreSyntheticClick` and `markTouchActivation` callbacks
 */
export function useSyntheticClickGuard(): SyntheticClickGuard {
  // States
  const lastTouchActivationRef = useRef(0);

  // Handlers
  const shouldIgnoreSyntheticClick = useCallback(() => {
    return Date.now() - lastTouchActivationRef.current < SYNTHETIC_CLICK_WINDOW_MS;
  }, []);

  const markTouchActivation = useCallback(() => {
    lastTouchActivationRef.current = Date.now();
  }, []);

  return { shouldIgnoreSyntheticClick, markTouchActivation };
}
